<script setup lang="ts">
import type { Component } from 'vue'

import BaseIcon from '@tix/ds/components/atoms/BaseIcon.vue'

type Props = {
  /** The main title text displayed in the heading (supports HTML). */
  title?: string
  /** Optional description text displayed below the title (supports HTML). */
  description?: string
  /** The HTML heading element to use (h2, h3, or h4). Choose based on document outline hierarchy. */
  headingTag?: 'h2' | 'h3' | 'h4'
  /** Optional leading icon beside the title. Also switches the title to the H2 treatment. */
  icon?: Component
}

withDefaults(defineProps<Props>(), {
  headingTag: 'h2',
})
</script>

<template>
  <header v-if="title || description" class="action-header" :class="{ 'has-icon': icon }">
    <div v-if="title" class="title-row">
      <BaseIcon v-if="icon" class="title-icon" :icon="icon" />

      <component :is="headingTag" v-shtml="title" class="title" />
    </div>

    <p v-if="description" v-shtml="description" class="description" />
  </header>
</template>

<style scoped>
.action-header {
  /* Local Themable Variables */
  --_font-size-lg: var(--action-header-font-size-lg, var(--size-lg));
  --_font-size-md: var(--action-header-font-size-md, var(--size-md));

  .title-row {
    display: flex;
    align-items: center;
    gap: var(--padding-cozy);
  }

  .title-icon.base-icon {
    width: 1.5rem;
    height: 1.5rem;
  }

  .title {
    flex: 1;
    min-width: 0;
    font-size: var(--_font-size-lg);
    font-weight: var(--font-weight-semibold);
    margin-bottom: var(--space-0-75);
    letter-spacing: 0;
    line-height: var(--line-height-heading);
  }

  .description {
    font-size: var(--_font-size-md);
    margin-bottom: var(--space-1-5);
    line-height: var(--line-height-body);
  }

  .description :deep(a) {
    text-decoration: underline;
  }

  &.has-icon {
    display: flex;
    flex-direction: column;
    gap: var(--padding-compact);

    .title {
      margin-bottom: 0;
      font-size: var(--font-size-h2);
      font-weight: var(--font-weight-bold);
    }

    .description {
      font-size: var(--font-size-body-small);
    }
  }
}
</style>
